import React, { useCallback, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Tooltip,
  Marker,
  CircleMarker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const API_BASE = (process.env.REACT_APP_API_BASE || "") + "/api/analysis";

const REPORT_TABS = [
  { key: "growth_opportunity", label: "Growth & Opportunity" },
  { key: "land_use_urban_structure", label: "Land Use & Urban Structure" },
  { key: "environmental_constraints", label: "Environmental Constraints" },
  { key: "infrastructure_feasibility", label: "Infrastructure & Feasibility" },
  { key: "policy_approval_logic", label: "Policy & Approval Logic" },
];

const MUNICIPALITY_COLORS = {
  vaughan: "#F6D6AD",
  markham: "#A7D5C5",
  "richmond hill": "#D7C6F3",
  newmarket: "#F5B7B1",
  aurora: "#BFE3E0",
  georgina: "#FFD9C7",
  "east gwillimbury": "#C7DDF5",
  "whitchurch-stouffville": "#E6D5F7",
  king: "#DDECC7",
};

function normalizeName(name) {
  return (name || "").toLowerCase().trim();
}

function municipalityStyle(feature) {
  const props = feature?.properties || {};

  const name = props.municipality || props.NAME || props.name || "";
  const normalized = normalizeName(name);
  const fillColor = MUNICIPALITY_COLORS[normalized] || "#cbd5e1";

  return {
    color: "#475569",
    weight: 2,
    fillColor: fillColor,
    fillOpacity: 0.5,
  };
}

function parcelStyle(feature, selectedParcelId) {
  const parcelId = feature?.properties?.PAR_GIS_ID;
  const isSelected = String(parcelId) === String(selectedParcelId);

  return {
    color: isSelected ? "#dc2626" : "#ff00ff",
    weight: isSelected ? 4 : 3,
    fillColor: isSelected ? "#f97316" : "#ff4dff",
    fillOpacity: isSelected ? 0.55 : 0.28,
  };
}

function getFeatureBounds(feature) {
  const geoJsonLayer = L.geoJSON(feature);
  return geoJsonLayer.getBounds();
}

function getFeatureCenter(feature) {
  const bounds = getFeatureBounds(feature);
  return bounds.getCenter();
}

function countMarkerIcon(count) {
  const text = String(count ?? 0);
  const width = Math.max(46, 18 + text.length * 10);

  return L.divIcon({
    className: "municipality-count-icon",
    html: `
      <div style="
        width:${width}px;
        height:44px;
        border-radius:22px;
        background:white;
        border:2px solid #64748b;
        display:flex;
        align-items:center;
        justify-content:center;
        font-weight:700;
        font-size:18px;
        color:#0f172a;
        box-shadow:0 8px 18px rgba(15,23,42,0.12);
      ">
        ${text}
      </div>
    `,
    iconSize: [width, 44],
    iconAnchor: [width / 2, 22],
  });
}

function getClusterRadius(count) {
  if (count < 10) return 12;
  if (count < 50) return 16;
  if (count < 200) return 20;
  if (count < 1000) return 24;
  if (count < 5000) return 30;
  return 36;
}

function getClusterLabelSize(count) {
  if (count < 100) return 12;
  if (count < 1000) return 13;
  if (count < 10000) return 14;
  return 15;
}

function getClusterStyle(count) {
  return {
    radius: getClusterRadius(count),
    fillOpacity:
      count < 50 ? 0.75 : count < 500 ? 0.78 : count < 5000 ? 0.82 : 0.88,
    weight: 2,
  };
}

function FitBoundsToFeature({ feature, onDone }) {
  const map = useMap();

  React.useEffect(() => {
    if (!feature) return;

    const bounds = getFeatureBounds(feature);
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }

    if (onDone) onDone();
  }, [feature, map, onDone]);

  return null;
}

function MapEventsBridge({ onBoundsChange, enabled }) {
  const timerRef = useRef(null);

  useMapEvents({
    moveend(e) {
      if (!enabled) return;
      const map = e.target;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onBoundsChange(map);
      }, 250);
    },
    zoomend(e) {
      if (!enabled) return;
      const map = e.target;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onBoundsChange(map);
      }, 250);
    },
  });

  return null;
}

function InitialMunicipalityLoader({ onLoad }) {
  const map = useMap();

  React.useEffect(() => {
    onLoad();
  }, [onLoad, map]);

  return null;
}

function FullScreenLoader({ show, text = "Loading..." }) {
  if (!show) return null;

  return (
    <div style={styles.loaderOverlay}>
      <div style={styles.loaderCard}>
        <div style={styles.spinner} />
        <div style={styles.loaderText}>{text}</div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={styles.infoRow}>
      <div style={styles.infoLabel}>{label}</div>
      <div style={styles.infoValue}>{value ?? "-"}</div>
    </div>
  );
}

function SummaryCard({ title, children }) {
  return (
    <div style={styles.summaryCard}>
      <h3 style={styles.summaryCardTitle}>{title}</h3>
      {children}
    </div>
  );
}

function EmptyPanel({ title, text }) {
  return (
    <div style={styles.emptyPanel}>
      <div style={styles.emptyPanelTitle}>{title}</div>
      <div style={styles.emptyPanelText}>{text}</div>
    </div>
  );
}

function StatusPill({ text, tone = "neutral" }) {
  const tones = {
    neutral: {
      background: "#f1f5f9",
      color: "#334155",
      border: "1px solid #cbd5e1",
    },
    good: {
      background: "#ecfdf5",
      color: "#166534",
      border: "1px solid #bbf7d0",
    },
    warn: {
      background: "#fff7ed",
      color: "#9a3412",
      border: "1px solid #fed7aa",
    },
    bad: {
      background: "#fef2f2",
      color: "#b91c1c",
      border: "1px solid #fecaca",
    },
  };

  return (
    <span style={{ ...styles.statusPill, ...(tones[tone] || tones.neutral) }}>
      {text}
    </span>
  );
}

function MetricCard({ label, value }) {
  return (
    <div style={styles.metricCard}>
      <div style={styles.metricLabel}>{label}</div>
      <div style={styles.metricValue}>{value}</div>
    </div>
  );
}

function SmallMeta({ label, value }) {
  return (
    <div style={styles.smallMeta}>
      <div style={styles.smallMetaLabel}>{label}</div>
      <div style={styles.smallMetaValue}>{value ?? "-"}</div>
    </div>
  );
}

function LayerStatusPill({ inside, within120m }) {
  let text = "No Constraint";
  let tone = "good";

  if (inside) {
    text = "Inside";
    tone = "bad";
  } else if (within120m) {
    text = "Near (120m)";
    tone = "warn";
  }

  return <StatusPill text={text} tone={tone} />;
}

function ConstraintLayerCard({
  title,
  tone = "neutral",
  children,
}) {
  const toneStyles = {
    neutral: {
      background: "#ffffff",
      border: "1px solid #e5e7eb",
    },
    green: {
      background: "#f0fdf4",
      border: "1px solid #bbf7d0",
    },
    woodland: {
      background: "#f8fafc",
      border: "1px solid #cbd5e1",
    },
  };

  return (
    <div
      style={{
        ...styles.constraintLayerCard,
        ...(toneStyles[tone] || toneStyles.neutral),
      }}
    >
      <div style={styles.constraintLayerHeader}>
        <h4 style={styles.constraintLayerTitle}>{title}</h4>
      </div>
      <div>{children}</div>
    </div>
  );
}

function ReportTabContent({ activeTab, parcelReport }) {
  if (!parcelReport) {
    return (
      <EmptyPanel
        title="No parcel selected"
        text="Click on a parcel to view its full report here."
      />
    );
  }

  if (activeTab === "environmental_constraints") {
    const summary = parcelReport?.constraint_summary || {};
    const checks = parcelReport?.checks || [];
    const props = parcelReport?.constraint_feature?.properties || {};

    const greenlands = {
      inside: props.inside_greenlands_system,
      className: props.greenlands_class,
      distance: props.distance_to_greenlands_m,
      within120m: props.within_greenlands_120m,
    };

    const woodlands = {
      inside: props.inside_woodland,
      distance: props.distance_to_woodland_m,
      within120m: props.within_woodland_120m,
    };

    return (
      <div style={styles.reportTabContent}>
        <div style={styles.reportHeroCard}>
          <div style={styles.reportHeroHeader}>
            <div>
              <div style={styles.reportHeroEyebrow}>Layer Report</div>
              <h3 style={styles.reportHeroTitle}>Environmental Constraints</h3>
            </div>
            <StatusPill
              text={summary?.status || "Unknown"}
              tone={
                summary?.hard_reject
                  ? "bad"
                  : Number(summary?.total_penalty || 0) > 0
                    ? "warn"
                    : "good"
              }
            />
          </div>

          <div style={styles.metricGrid}>
            <MetricCard
              label="Hard Reject"
              value={summary?.hard_reject ? "Yes" : "No"}
            />
            <MetricCard
              label="Total Penalty"
              value={summary?.total_penalty ?? 0}
            />
            <MetricCard
              label="Checks Returned"
              value={checks?.length ?? 0}
            />
          </div>

          <div style={styles.messageBox}>
            {summary?.message || "No environmental summary returned."}
          </div>
        </div>

        <div style={styles.layerGrid}>
          <ConstraintLayerCard title="Greenlands Layer" tone="green">
            <div style={styles.layerTopStatus}>
              <LayerStatusPill
                inside={greenlands.inside}
                within120m={greenlands.within120m}
              />
            </div>

            <div style={styles.infoGrid}>
              <InfoChip
                label="Inside Greenlands"
                value={greenlands.inside ? "Yes" : "No"}
              />
              <InfoChip
                label="Greenlands Class"
                value={greenlands.className || "-"}
              />
              <InfoChip
                label="Distance"
                value={
                  greenlands.distance != null
                    ? `${Math.round(greenlands.distance)} m`
                    : "-"
                }
              />
              <InfoChip
                label="Within 120m"
                value={greenlands.within120m ? "Yes" : "No"}
              />
            </div>

            <div style={styles.layerDescription}>
              {greenlands.inside
                ? "This parcel is located inside the Greenlands system."
                : greenlands.within120m
                  ? "This parcel is outside Greenlands but is still within the 120m influence area."
                  : "This parcel is not currently affected by the Greenlands proximity threshold."}
            </div>
          </ConstraintLayerCard>

          <ConstraintLayerCard title="Woodlands Layer" tone="woodland">
            <div style={styles.layerTopStatus}>
              <LayerStatusPill
                inside={woodlands.inside}
                within120m={woodlands.within120m}
              />
            </div>

            <div style={styles.infoGrid}>
              <InfoChip
                label="Inside Woodland"
                value={woodlands.inside ? "Yes" : "No"}
              />
              <InfoChip
                label="Distance"
                value={
                  woodlands.distance != null
                    ? `${Math.round(woodlands.distance)} m`
                    : "-"
                }
              />
              <InfoChip
                label="Within 120m"
                value={woodlands.within120m ? "Yes" : "No"}
              />
            </div>

            <div style={styles.layerDescription}>
              {woodlands.inside
                ? "This parcel is located inside a woodland area."
                : woodlands.within120m
                  ? "This parcel is close to a woodland area and may require additional environmental attention."
                  : "This parcel is not currently affected by the woodland proximity threshold."}
            </div>
          </ConstraintLayerCard>
        </div>

        <div style={styles.reportSection}>
          <h4 style={styles.reportSectionTitle}>Detailed Checks</h4>

          {checks?.length ? (
            <div style={styles.checkGrid}>
              {checks.map((check, idx) => (
                <div key={idx} style={styles.checkCard}>
                  <div style={styles.checkCardTop}>
                    <div style={styles.checkTitle}>{check.label || "Check"}</div>
                    <StatusPill
                      text={check.severity || "n/a"}
                      tone={
                        check.severity === "high"
                          ? "bad"
                          : check.severity === "moderate"
                            ? "warn"
                            : "neutral"
                      }
                    />
                  </div>

                  <div style={styles.checkMetaGrid}>
                    <SmallMeta label="Type" value={check.type} />
                    <SmallMeta label="Result" value={String(check.result)} />
                    <SmallMeta label="Severity" value={check.severity} />
                  </div>

                  <div style={styles.checkMessage}>
                    {check.message || "No message returned."}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyPanel
              title="No checks returned"
              text="The backend has not returned any environmental checks for this parcel yet."
            />
          )}
        </div>
      </div>
    );
  }

  const fallbackMap = {
    growth_opportunity: {
      title: "Growth & Opportunity",
      text: "This tab is ready. The report will appear here as soon as the backend returns this layer.",
      raw: parcelReport?.growth_opportunity,
    },
    land_use_urban_structure: {
      title: "Land Use & Urban Structure",
      text: "Land use, urban structure, zoning and built-form information will be shown here.",
      raw: parcelReport?.land_use_urban_structure,
    },
    infrastructure_feasibility: {
      title: "Infrastructure & Feasibility",
      text: "Distances to infrastructure, access, transportation and feasibility will be shown here.",
      raw: parcelReport?.infrastructure_feasibility,
    },
    policy_approval_logic: {
      title: "Policy & Approval Logic",
      text: "Analysis of policy alignment, approval risk, review logic and the approval path will be shown here.",
      raw: parcelReport?.policy_approval_logic,
    },
  };

  const item = fallbackMap[activeTab];

  if (!item) {
    return (
      <EmptyPanel
        title="No content"
        text="No data was found for this tab."
      />
    );
  }

  return (
    <div style={styles.reportTabContent}>
      <div style={styles.reportHeroCard}>
        <div style={styles.reportHeroEyebrow}>Layer Report</div>
        <h3 style={styles.reportHeroTitle}>{item.title}</h3>
        <div style={styles.messageBoxSoft}>{item.text}</div>
      </div>

      {item.raw ? (
        <div style={styles.reportSection}>
          <h4 style={styles.reportSectionTitle}>Returned Data</h4>
          <pre style={styles.preBlock}>
            {JSON.stringify(item.raw, null, 2)}
          </pre>
        </div>
      ) : (
        <EmptyPanel
          title="Waiting for backend data"
          text="This tab is UI-only for now. It will be filled in once the backend returns this layer."
        />
      )}
    </div>
  );
}

function InfoChip({ label, value }) {
  return (
    <div style={styles.infoChip}>
      <div style={styles.infoChipLabel}>{label}</div>
      <div style={styles.infoChipValue}>{value ?? "-"}</div>
    </div>
  );
}

export default function ParcelMapPage() {
  const [viewMode, setViewMode] = useState("municipality");
  const [municipalityData, setMunicipalityData] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [selectedMunicipalityFeature, setSelectedMunicipalityFeature] =
    useState(null);
  const [selectedParcelId, setSelectedParcelId] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [parcelReport, setParcelReport] = useState(null);
  const [loadingMap, setLoadingMap] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState("");
  const [shouldFitToMunicipality, setShouldFitToMunicipality] = useState(false);
  const [activeTab, setActiveTab] = useState("environmental_constraints");

  const latestRequestRef = useRef(0);

  const loadMunicipalitySummary = useCallback(async () => {
    try {
      latestRequestRef.current = 0;
      setLoadingMap(true);
      setError("");
      setViewMode("municipality");
      setSelectedMunicipality(null);
      setSelectedMunicipalityFeature(null);
      setSelectedParcelId(null);
      setSelectedFeature(null);
      setParcelReport(null);
      setMapData(null);
      setShouldFitToMunicipality(false);
      setActiveTab("environmental_constraints");

      const res = await axios.get(`${API_BASE}/parcel-municipality-summary/`);
      setMunicipalityData(res.data);
    } catch (err) {
      console.error(err);
      setError("Failed to load municipality summary.");
    } finally {
      setLoadingMap(false);
    }
  }, []);

  const fetchMunicipalityMapData = useCallback(
    async (map) => {
      if (!selectedMunicipality || !map) return;

      const requestId = Date.now();
      latestRequestRef.current = requestId;

      try {
        setLoadingMap(true);
        setError("");

        const bounds = map.getBounds();
        const zoom = map.getZoom();

        const buffer =
          zoom < 10 ? 0.22 : zoom < 12 ? 0.08 : zoom < 14 ? 0.04 : 0.015;

        const minx = bounds.getWest() - buffer;
        const miny = bounds.getSouth() - buffer;
        const maxx = bounds.getEast() + buffer;
        const maxy = bounds.getNorth() + buffer;

        const center = map.getCenter();
        const rawZoom = map.getZoom();
        const requestZoom = rawZoom;

        const res = await axios.get(`${API_BASE}/municipality-map-data/`, {
          params: {
            municipality: selectedMunicipality,
            minx,
            miny,
            maxx,
            maxy,
            zoom: requestZoom,
            center_lng: center.lng,
            center_lat: center.lat,
          },
        });

        console.log("municipality-map-data response:", res.data);
        console.log("mode:", res.data?.mode);
        console.log("returned_count:", res.data?.returned_count);
        console.log("features length:", res.data?.features?.length);

        if (latestRequestRef.current !== requestId) return;
        setMapData(res.data);
      } catch (err) {
        if (latestRequestRef.current === requestId) {
          console.error(err);
          setError("Failed to load municipality map data.");
        }
      } finally {
        if (latestRequestRef.current === requestId) {
          setLoadingMap(false);
        }
      }
    },
    [selectedMunicipality]
  );

  const handleMunicipalitySelect = useCallback((feature) => {
    const municipality = feature?.properties?.municipality;
    if (!municipality) return;

    latestRequestRef.current = 0;

    setViewMode("parcels");
    setSelectedMunicipality(municipality);
    setSelectedMunicipalityFeature(feature);
    setSelectedParcelId(null);
    setSelectedFeature(null);
    setParcelReport(null);
    setMapData(null);
    setShouldFitToMunicipality(true);
    setActiveTab("environmental_constraints");
  }, []);

  const handleParcelClick = useCallback(async (feature) => {
    const parcelId = feature?.properties?.PAR_GIS_ID;
    if (!parcelId) return;

    try {
      setSelectedParcelId(parcelId);
      setSelectedFeature(feature);
      setLoadingReport(true);
      setParcelReport(null);
      setError("");
      setActiveTab("environmental_constraints");

      const res = await axios.post(`${API_BASE}/parcel-report/`, {
        parcel_id: parcelId,
      });
      console.log("PARCEL REPORT RESPONSE:", res.data);

      setParcelReport(res.data);
    } catch (err) {
      console.error(err);
      setError("Failed to load parcel report.");
    } finally {
      setLoadingReport(false);
    }
  }, []);

  const onEachMunicipality = useCallback(
    (feature, layer) => {
      const props = feature?.properties || {};

      layer.on({
        click: () => handleMunicipalitySelect(feature),
      });

      layer.bindTooltip(
        `
          <div>
            <strong>${props.municipality || props.NAME || "Municipality"}</strong><br/>
            Parcels: ${props.parcel_count ?? 0}
          </div>
        `,
        { sticky: true }
      );
    },
    [handleMunicipalitySelect]
  );

  const onEachParcel = useCallback(
    (feature, layer) => {
      const props = feature?.properties || {};

      layer.on({
        click: () => handleParcelClick(feature),
      });

      layer.bindTooltip(
        `
          <div>
            <strong>${props.LOCATION || "No address"}</strong><br/>
            Parcel ID: ${props.PAR_GIS_ID || "-"}<br/>
            Municipality: ${props.MUNNAME || "-"}
          </div>
        `,
        { sticky: true }
      );
    },
    [handleParcelClick]
  );

  const isBusy = loadingMap || loadingReport;

  const mapModeLabel = useMemo(() => {
    if (viewMode === "municipality") return "Mode: Municipalities";
    if (mapData?.mode === "clusters") {
      return `Mode: Clusters in ${selectedMunicipality || ""}`;
    }
    return `Mode: Parcels in ${selectedMunicipality || ""}`;
  }, [viewMode, mapData, selectedMunicipality]);

  return (
    <div style={styles.page}>
      <FullScreenLoader
        show={isBusy}
        text={loadingMap ? "Loading map data..." : "Loading parcel report..."}
      />

      <div style={styles.mainColumn}>
        <div style={styles.topSection}>
          <div style={styles.mapCard}>
            <div style={styles.mapCardHeader}>
              <div>
                <div style={styles.pageEyebrow}>UrbanScore Parcel View</div>
                <h1 style={styles.pageTitle}>Parcel Map Explorer</h1>
              </div>
            </div>

            <div style={styles.mapWrap}>
              <MapContainer
                center={[43.88, -79.43]}
                zoom={10}
                style={{ height: "100%", width: "100%" }}
              >
                <InitialMunicipalityLoader onLoad={loadMunicipalitySummary} />

                <TileLayer
                  attribution="&copy; OpenStreetMap contributors"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <MapEventsBridge
                  enabled={viewMode === "parcels" && !!selectedMunicipality}
                  onBoundsChange={fetchMunicipalityMapData}
                />

                {viewMode === "municipality" && municipalityData && (
                  <>
                    <GeoJSON
                      data={municipalityData}
                      style={municipalityStyle}
                      onEachFeature={onEachMunicipality}
                    />

                    {municipalityData.features?.map((feature, idx) => {
                      const center = getFeatureCenter(feature);
                      const municipality =
                        feature?.properties?.municipality ||
                        feature?.properties?.NAME ||
                        "";
                      const parcelCount =
                        feature?.properties?.parcel_count ?? 0;

                      return (
                        <Marker
                          key={`municipality-marker-${idx}-${municipality}`}
                          position={[center.lat, center.lng]}
                          icon={countMarkerIcon(parcelCount)}
                          eventHandlers={{
                            click: () => handleMunicipalitySelect(feature),
                          }}
                        >
                          <Tooltip direction="top" offset={[0, -18]} opacity={1}>
                            <div>
                              <strong>{municipality}</strong>
                              <br />
                              Parcels: {parcelCount}
                            </div>
                          </Tooltip>
                        </Marker>
                      );
                    })}
                  </>
                )}

                {viewMode === "parcels" &&
                  selectedMunicipalityFeature &&
                  shouldFitToMunicipality && (
                    <FitBoundsToFeature
                      feature={selectedMunicipalityFeature}
                      onDone={() => setShouldFitToMunicipality(false)}
                    />
                  )}

                {viewMode === "parcels" &&
                  mapData?.mode === "clusters" &&
                  mapData.items?.map((item, idx) => {
                    const clusterStyle = getClusterStyle(item.count);

                    return (
                      <CircleMarker
                        key={`cluster-${idx}`}
                        center={[item.lat, item.lng]}
                        pathOptions={{
                          color: "#f97316",
                          fillColor: "#fb923c",
                        }}
                        radius={clusterStyle.radius}
                        fillOpacity={clusterStyle.fillOpacity}
                        weight={clusterStyle.weight}
                      >
                        <Tooltip permanent direction="center" opacity={1}>
                          <span
                            style={{
                              color: "#111827",
                              fontWeight: 700,
                              fontSize: `${getClusterLabelSize(item.count)}px`,
                              backgroundColor: "transparent",
                            }}
                          >
                            {item.count}
                          </span>
                        </Tooltip>
                      </CircleMarker>
                    );
                  })}

                {viewMode === "parcels" && mapData?.mode === "parcels" && (
                  <GeoJSON
                    key={`parcels-${selectedMunicipality || "none"}-${selectedParcelId || "none"}-${mapData?.returned_count || 0}`}
                    data={mapData}
                    style={(feature) => parcelStyle(feature, selectedParcelId)}
                    onEachFeature={onEachParcel}
                    eventHandlers={{
                      add: () => console.log("Parcel GeoJSON layer added to map"),
                    }}
                  />
                )}
              </MapContainer>

              {!isBusy && <div style={styles.mapBadge}>{mapModeLabel}</div>}

              {viewMode === "parcels" && !isBusy && (
                <button
                  style={styles.backButton}
                  onClick={loadMunicipalitySummary}
                >
                  Back to Municipalities
                </button>
              )}
            </div>
          </div>

          <aside style={styles.summaryPanel}>
            <div style={styles.summaryPanelHeader}>
              <h2 style={styles.summaryPanelTitle}>Parcel Summary</h2>
              <div style={styles.summaryPanelSub}>
                Summary information scrolls within this panel.
              </div>
            </div>

            <div style={styles.summaryScrollArea}>
              {error ? <div style={styles.errorBox}>{error}</div> : null}

              {viewMode === "municipality" && (
                <SummaryCard title="Guide">
                  <div style={styles.guideText}>
                    Municipal boundaries are displayed here. Click on any municipality to enter its parcel view.
                  </div>
                </SummaryCard>
              )}

              {selectedMunicipality && (
                <SummaryCard title="Selected Municipality">
                  <InfoRow label="Name" value={selectedMunicipality} />
                  <InfoRow label="Map Mode" value={mapData?.mode || "-"} />
                  <InfoRow
                    label="Returned Count"
                    value={mapData?.returned_count ?? 0}
                  />
                </SummaryCard>
              )}

              {selectedFeature && (
                <SummaryCard title="Selected Parcel">
                  <InfoRow
                    label="Parcel ID"
                    value={selectedFeature.properties?.PAR_GIS_ID}
                  />
                  <InfoRow
                    label="Address"
                    value={selectedFeature.properties?.LOCATION}
                  />
                  <InfoRow
                    label="Municipality"
                    value={selectedFeature.properties?.MUNNAME}
                  />
                  <InfoRow
                    label="Type"
                    value={selectedFeature.properties?.PAR_TYPE}
                  />
                  <InfoRow
                    label="Status"
                    value={selectedFeature.properties?.LIFESTATUS}
                  />
                  <InfoRow
                    label="Area"
                    value={selectedFeature.properties?.Shape__Area}
                  />
                </SummaryCard>
              )}

              <SummaryCard title="Overall Result">
                {parcelReport ? (
                  <>
                    <InfoRow
                      label="Constraint Status"
                      value={parcelReport.constraint_summary?.status}
                    />
                    <InfoRow
                      label="Hard Reject"
                      value={
                        parcelReport.constraint_summary?.hard_reject
                          ? "Yes"
                          : "No"
                      }
                    />
                    <InfoRow
                      label="Penalty"
                      value={parcelReport.constraint_summary?.total_penalty}
                    />
                  </>
                ) : (
                  <div style={styles.guideText}>
                    Once a parcel is selected, the overall result will be displayed here.
                  </div>
                )}
              </SummaryCard>
            </div>
          </aside>
        </div>

        <div style={styles.bottomReportSection}>
          <div style={styles.reportHeader}>
            <div>
              <div style={styles.pageEyebrow}>Parcel Analysis</div>
              <h2 style={styles.reportMainTitle}>5-Layer Parcel Report</h2>
            </div>
          </div>

          <div style={styles.tabsBar}>
            {REPORT_TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    ...styles.tabButton,
                    ...(isActive ? styles.tabButtonActive : {}),
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div style={styles.reportBody}>
            <ReportTabContent activeTab={activeTab} parcelReport={parcelReport} />
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    position: "relative",
    width: "100%",
    minHeight: "100vh",
    background: "#f8fafc",
    overflow: "hidden",
  },

  mainColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
    padding: 18,
    minHeight: "100vh",
    boxSizing: "border-box",
  },

  topSection: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 380px",
    gap: 18,
    height: "58vh",
    minHeight: "58vh",
    maxHeight: "58vh",
    alignItems: "stretch",
  },

  mapCard: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 24,
    boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
  },

  mapCardHeader: {
    padding: "18px 20px 12px 20px",
    borderBottom: "1px solid #eef2f7",
  },

  pageEyebrow: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#f97316",
    marginBottom: 6,
  },

  pageTitle: {
    margin: 0,
    fontSize: 28,
    lineHeight: 1.1,
    color: "#0f172a",
  },

  mapWrap: {
    position: "relative",
    flex: 1,
    minHeight: 420,
  },

  summaryPanel: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 24,
    boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    maxHeight: "100%",
    overflow: "hidden",
  },

  summaryPanelHeader: {
    padding: 20,
    borderBottom: "1px solid #eef2f7",
  },

  summaryPanelTitle: {
    margin: 0,
    fontSize: 22,
    lineHeight: 1.1,
    color: "#0f172a",
  },

  summaryPanelSub: {
    marginTop: 6,
    color: "#64748b",
    fontSize: 13,
  },

  summaryScrollArea: {
    padding: 16,
    overflowY: "auto",
    flex: 1,
    minHeight: 0,
    maxHeight: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },

  summaryCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 18,
    padding: 16,
    background: "#f8fafc",
  },

  summaryCardTitle: {
    margin: "0 0 12px 0",
    fontSize: 18,
    color: "#0f172a",
  },

  guideText: {
    color: "#475569",
    lineHeight: 1.8,
    fontSize: 14,
  },

  bottomReportSection: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 24,
    boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: "34vh",
  },

  reportHeader: {
    padding: "18px 20px 10px 20px",
    borderBottom: "1px solid #eef2f7",
  },

  reportMainTitle: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.1,
    color: "#0f172a",
  },

  tabsBar: {
    display: "flex",
    gap: 10,
    padding: "14px 18px",
    flexWrap: "wrap",
    borderBottom: "1px solid #eef2f7",
    background: "#fcfcfd",
  },

  tabButton: {
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    borderRadius: 999,
    padding: "10px 14px",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 700,
    color: "#334155",
    transition: "all 0.2s ease",
  },

  tabButtonActive: {
    background: "#fff7ed",
    color: "#c2410c",
    border: "1px solid #fdba74",
    boxShadow: "0 6px 14px rgba(249,115,22,0.12)",
  },

  reportBody: {
    padding: 18,
  },

  reportTabContent: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },

  reportHeroCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 20,
    background: "#f8fafc",
    padding: 18,
  },

  reportHeroHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "flex-start",
    marginBottom: 16,
  },

  reportHeroEyebrow: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#f97316",
    marginBottom: 6,
  },

  reportHeroTitle: {
    margin: 0,
    fontSize: 26,
    lineHeight: 1.15,
    color: "#0f172a",
  },

  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14,
    marginBottom: 16,
  },

  metricCard: {
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    borderRadius: 16,
    padding: 16,
  },

  metricLabel: {
    fontSize: 13,
    color: "#64748b",
    marginBottom: 8,
    fontWeight: 600,
  },

  metricValue: {
    fontSize: 24,
    fontWeight: 800,
    color: "#0f172a",
  },

  reportSection: {
    border: "1px solid #e5e7eb",
    borderRadius: 20,
    background: "#ffffff",
    padding: 18,
  },

  reportSectionTitle: {
    margin: "0 0 16px 0",
    fontSize: 20,
    color: "#0f172a",
  },

  layerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 16,
  },

  constraintLayerCard: {
    borderRadius: 20,
    padding: 18,
  },

  constraintLayerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 14,
  },

  constraintLayerTitle: {
    margin: 0,
    fontSize: 20,
    color: "#0f172a",
  },

  layerTopStatus: {
    marginBottom: 14,
  },

  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
    gap: 12,
    marginBottom: 14,
  },

  infoChip: {
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: 12,
    background: "#ffffff",
  },

  infoChipLabel: {
    fontSize: 12,
    color: "#64748b",
    fontWeight: 700,
    marginBottom: 6,
  },

  infoChipValue: {
    fontSize: 15,
    color: "#0f172a",
    fontWeight: 800,
    wordBreak: "break-word",
  },

  layerDescription: {
    color: "#475569",
    lineHeight: 1.75,
    fontSize: 14,
  },

  checkGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 14,
  },

  checkCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 18,
    background: "#f8fafc",
    padding: 16,
  },

  checkCardTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "flex-start",
    marginBottom: 12,
  },

  checkTitle: {
    fontWeight: 800,
    color: "#0f172a",
    fontSize: 16,
    lineHeight: 1.35,
  },

  checkMetaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 10,
    marginBottom: 12,
  },

  smallMeta: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#ffffff",
    padding: 10,
  },

  smallMetaLabel: {
    color: "#64748b",
    fontSize: 12,
    marginBottom: 4,
    fontWeight: 700,
  },

  smallMetaValue: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 700,
    wordBreak: "break-word",
  },

  emptyPanel: {
    border: "1px dashed #cbd5e1",
    borderRadius: 18,
    background: "#f8fafc",
    padding: 18,
  },

  emptyPanelTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: 8,
  },

  emptyPanelText: {
    color: "#475569",
    lineHeight: 1.7,
  },

  preBlock: {
    margin: 0,
    padding: 16,
    background: "#0f172a",
    color: "#e2e8f0",
    borderRadius: 16,
    overflowX: "auto",
    fontSize: 13,
    lineHeight: 1.6,
  },

  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: "9px 0",
    borderBottom: "1px solid #e5e7eb",
  },

  infoLabel: {
    fontWeight: 700,
    color: "#334155",
  },

  infoValue: {
    color: "#0f172a",
    textAlign: "right",
    wordBreak: "break-word",
  },

  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 34,
    borderRadius: 999,
    padding: "6px 12px",
    fontWeight: 800,
    fontSize: 13,
    whiteSpace: "nowrap",
  },

  messageBox: {
    padding: 14,
    borderRadius: 14,
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    color: "#9a3412",
    lineHeight: 1.7,
  },

  messageBoxSoft: {
    padding: 14,
    borderRadius: 14,
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    color: "#475569",
    lineHeight: 1.7,
    marginTop: 12,
  },

  errorBox: {
    padding: 14,
    borderRadius: 14,
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#b91c1c",
    fontWeight: 700,
  },

  loaderOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 5000,
    background: "rgba(255,255,255,0.72)",
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  loaderCard: {
    minWidth: 220,
    padding: "28px 32px",
    borderRadius: 24,
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(229,231,235,0.9)",
    boxShadow: "0 18px 50px rgba(15,23,42,0.12)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
  },

  spinner: {
    width: 54,
    height: 54,
    borderRadius: "50%",
    border: "5px solid #e5e7eb",
    borderTop: "5px solid #f97316",
    animation: "urbanScoreSpin 0.9s linear infinite",
  },

  loaderText: {
    fontSize: 16,
    fontWeight: 700,
    color: "#1f2937",
    letterSpacing: "0.01em",
  },

  mapBadge: {
    position: "absolute",
    top: 16,
    left: 16,
    zIndex: 1000,
    background: "rgba(255,255,255,0.96)",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: "10px 14px",
    fontSize: 14,
    fontWeight: 700,
    boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
  },

  backButton: {
    position: "absolute",
    top: 68,
    left: 16,
    zIndex: 1000,
    background: "#ffffff",
    border: "1px solid #d1d5db",
    borderRadius: 14,
    padding: "10px 14px",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 700,
    boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
  },
};

const styleTag = document.createElement("style");
styleTag.innerHTML = `
  @keyframes urbanScoreSpin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @media (max-width: 1100px) {
    .leaflet-container {
      min-height: 380px;
    }
  }
`;

if (!document.getElementById("urbanscore-loader-style")) {
  styleTag.id = "urbanscore-loader-style";
  document.head.appendChild(styleTag);
}